import React, { useState, useRef, useEffect } from 'react';
import { motion, Variants } from 'framer-motion';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } },
};

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.02 } },
};

const SUGGESTED_TOPICS = [
  'Should remote work be the default?',
  'Is universal basic income feasible?',
  'Should social media require age verification?',
  'Is nuclear energy the answer to climate change?',
  'Should college be free?',
  'Is a four-day work week inevitable?',
];

interface DebateSubmissionProps {
  onSubmit: (topic: string) => void;
  isDisabled: boolean;
  remainingDebates: number;
}

const DebateSubmission: React.FC<DebateSubmissionProps> = ({
  onSubmit,
  isDisabled,
  remainingDebates,
}) => {
  const [topic, setTopic] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [moderationStatus, setModerationStatus] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [topic]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!topic.trim() || isSubmitting || isDisabled) return;

    setIsSubmitting(true);
    setModerationStatus('Checking content…');
    try {
      await onSubmit(topic.trim());
      setTopic('');
      setModerationStatus('');
    } catch (error) {
      setModerationStatus('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const pickSuggestion = (suggestion: string) => {
    setTopic(suggestion);
    textareaRef.current?.focus();
  };

  return (
    <motion.div
      initial="hidden"
      animate="show"
      variants={stagger}
      className="mx-auto w-full max-w-2xl"
    >
      {/* Page header */}
      <motion.div variants={fadeUp} className="text-center">
        <span className="eyebrow inline-flex items-center gap-2 rounded-full border border-black/[0.08] bg-white/80 px-4 py-2">
          <span className="h-1.5 w-1.5 rounded-full bg-steel-blue" />
          New debate
          <span className="h-1.5 w-1.5 rounded-full bg-vermilion" />
        </span>
        <h2 className="mt-5 text-balance font-serif text-4xl tracking-tight text-charcoal sm:text-5xl">
          Pose the question.
        </h2>
        <p className="mx-auto mt-3 max-w-md text-pretty text-base leading-relaxed text-ink-muted">
          Two AI agents will research it, then argue the strongest case for each side
          — live, in front of you.
        </p>
      </motion.div>

      {/* Form card */}
      <motion.div variants={fadeUp} className="card-frame mt-9 overflow-hidden">
        <form onSubmit={handleSubmit} className="p-6 sm:p-7">
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={topic}
              onChange={e => setTopic(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a balanced question — e.g. 'Should remote work be the default?'"
              disabled={isDisabled || isSubmitting}
              autoFocus
              className="w-full resize-none overflow-hidden rounded-xl border border-black/[0.08] bg-white px-4 py-3.5 pr-14 font-serif text-lg leading-snug text-charcoal placeholder:font-sans placeholder:text-base placeholder-ink-muted/50 transition-colors focus:border-charcoal/30 focus:outline-none focus:ring-2 focus:ring-charcoal/10 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ minHeight: '76px', maxHeight: '220px' }}
            />

            <button
              type="submit"
              disabled={!topic.trim() || isSubmitting || isDisabled}
              aria-label="Start the debate"
              className="absolute bottom-3 right-3 rounded-full bg-charcoal p-2.5 text-white transition-all duration-200 ease-out-expo hover:-translate-y-0.5 hover:shadow-lift disabled:cursor-not-allowed disabled:bg-charcoal/20 disabled:hover:translate-y-0 disabled:hover:shadow-none"
            >
              {isSubmitting ? (
                <div className="relative h-5 w-5">
                  <div className="absolute inset-0 rounded-full border-2 border-white/20" />
                  <div className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-white" />
                </div>
              ) : (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          </div>

          <div className="mt-3 flex items-center justify-between text-xs text-ink-muted">
            {moderationStatus ? (
              <span className="flex items-center gap-2 text-ink-muted">
                <div className="relative h-3.5 w-3.5">
                  <div className="absolute inset-0 rounded-full border border-charcoal/20" />
                  <div className="absolute inset-0 animate-spin rounded-full border border-transparent border-t-charcoal/60" />
                </div>
                {moderationStatus}
              </span>
            ) : (
              <span className="font-mono text-[11px] uppercase tracking-wider text-ink-muted/70">
                Enter to start · Shift+Enter for new line
              </span>
            )}
            {topic.length > 0 && !moderationStatus && (
              <span className={`font-mono ${topic.length < 10 ? 'text-vermilion' : ''}`}>
                {topic.length} chars{topic.length < 10 && ' (min 10)'}
              </span>
            )}
          </div>
        </form>

        {/* Credit footer */}
        <div className="flex items-center justify-between border-t border-hairline px-6 py-3.5 sm:px-7">
          <span className="font-mono text-[11px] uppercase tracking-wider text-ink-muted">
            1 credit per debate
          </span>
          {remainingDebates > 0 ? (
            <span className={`font-mono text-[11px] uppercase tracking-wider ${remainingDebates <= 3 ? 'text-vermilion-deep' : 'text-ink-muted'}`}>
              {remainingDebates} credit{remainingDebates !== 1 ? 's' : ''} left
            </span>
          ) : (
            <span className="font-mono text-[11px] uppercase tracking-wider text-vermilion-deep">
              No credits — upgrade to continue
            </span>
          )}
        </div>
      </motion.div>

      {/* Suggested topics */}
      <motion.div variants={fadeUp} className="mt-8 text-center">
        <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-muted/70">
          Or borrow one of these
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {SUGGESTED_TOPICS.map(suggestion => (
            <button
              key={suggestion}
              type="button"
              onClick={() => pickSuggestion(suggestion)}
              disabled={isDisabled || isSubmitting}
              className={`inline-flex items-center gap-2.5 rounded-full border bg-white px-4 py-2 font-mono text-xs transition-all duration-200 ease-out-expo disabled:cursor-not-allowed disabled:opacity-50 ${
                topic === suggestion
                  ? 'border-charcoal text-charcoal'
                  : 'border-black/[0.08] text-ink-muted hover:-translate-y-0.5 hover:border-charcoal hover:text-charcoal'
              }`}
            >
              <span className="flex h-2 w-2 overflow-hidden rounded-full">
                <span className="h-full w-1/2 bg-steel-blue" />
                <span className="h-full w-1/2 bg-vermilion" />
              </span>
              {suggestion}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Tips */}
      <motion.div
        variants={fadeUp}
        className="mt-10 grid grid-cols-1 divide-y divide-black/[0.06] overflow-hidden rounded-2xl border border-black/[0.08] bg-white/80 backdrop-blur-sm sm:grid-cols-3 sm:divide-x sm:divide-y-0"
      >
        {[
          ['Stay neutral', "Frame the question so it doesn't presuppose an answer."],
          ['Pick a real contest', 'Both sides should have legitimate arguments.'],
          ['Be specific', 'Narrow questions produce sharper debates.'],
        ].map(([title, body]) => (
          <div key={title} className="px-5 py-4">
            <p className="text-xs font-semibold text-charcoal">{title}</p>
            <p className="mt-1 text-pretty text-xs leading-relaxed text-ink-muted">{body}</p>
          </div>
        ))}
      </motion.div>
    </motion.div>
  );
};

export default DebateSubmission;
